import { Injectable } from '@angular/core';
import {
  bridgeFail,
  bridgeOk,
  type BridgeResult,
  type NativeBridge,
  type StethoscopeStats,
  type UsbDeviceState,
} from './native-bridge';
import { encodeFrame } from '@core/audio/pcm-frame';

/**
 * In-browser stand-in for the Android host.
 *
 * This is not only a development convenience — it is what makes D22's tests 4
 * and 5 runnable at all. Both assert properties of the session lifecycle
 * (the microphone track is never rebuilt; teardown leaves no residue) that
 * otherwise could only be checked on a phone with a stethoscope attached, which
 * in practice means they would not be checked.
 *
 * It also records what it was asked to do, so a test can assert the *sequence*
 * rather than just the outcome — ordering is the entire subject of §3.5.
 */
@Injectable()
export class MockNativeBridge implements NativeBridge {
  /** Ordered log of bridge calls, for asserting §3.5 step order. */
  readonly calls: string[] = [];

  /** Flip to exercise the failure paths. */
  failSelectBuiltIn = false;
  failRouteSettle = false;
  failPinOutput = false;

  /**
   * What `currentStethoscopeState` answers. Null answers with a failure, as a
   * host without the query would — which keeps the page on push-only updates,
   * the behaviour every existing test was written against.
   */
  initialStethoscopeState: UsbDeviceState | null = null;

  private stethoRunning = false;
  private sessionActive = false;
  private sequence = 0;
  private generator: number | null = null;

  private pcmPortHandler: ((port: MessagePort) => void) | null = null;
  private deviceStateHandler: ((state: UsbDeviceState) => void) | null = null;
  private errorHandler: ((message: string) => void) | null = null;
  private routeRestoredHandler: ((message: string) => void) | null = null;

  private channel: MessageChannel | null = null;

  isNative(): boolean {
    return false;
  }

  async startSession(sessionId: string): Promise<BridgeResult> {
    this.calls.push(`startSession:${sessionId}`);
    this.sessionActive = true;
    this.sequence = 0;
    return bridgeOk(undefined);
  }

  async endSession(reason: string): Promise<BridgeResult> {
    this.calls.push(`endSession:${reason}`);
    this.stopGenerator();
    this.stethoRunning = false;
    this.sessionActive = false;
    this.closeChannel();
    return bridgeOk(undefined);
  }

  async selectBuiltInCommDevice(): Promise<BridgeResult> {
    this.calls.push('selectBuiltInCommDevice');
    return this.failSelectBuiltIn
      ? bridgeFail('mock: built-in selection failed')
      : bridgeOk(undefined);
  }

  async awaitRouteSettled(timeoutMs: number): Promise<BridgeResult> {
    this.calls.push(`awaitRouteSettled:${timeoutMs}`);
    return this.failRouteSettle
      ? bridgeFail('mock: route did not settle')
      : bridgeOk(undefined);
  }

  async pinOutputToHdmi(): Promise<BridgeResult> {
    this.calls.push('pinOutputToHdmi');
    return this.failPinOutput
      ? bridgeFail('mock: no HDMI output present')
      : bridgeOk(undefined);
  }

  async startStethoscope(): Promise<BridgeResult> {
    this.calls.push('startStethoscope');
    this.stethoRunning = true;
    return bridgeOk(undefined);
  }

  async stopStethoscope(): Promise<BridgeResult> {
    this.calls.push('stopStethoscope');
    this.stopGenerator();
    this.stethoRunning = false;
    return bridgeOk(undefined);
  }

  async stethoscopeStats(): Promise<BridgeResult<StethoscopeStats>> {
    return bridgeOk({
      running: this.stethoRunning,
      sampleRate: 48_000,
      packetsOk: this.sequence,
      packetsDropped: 0,
      overruns: 0,
      deviceGone: false,
    });
  }

  async notifyJoined(): Promise<BridgeResult> {
    this.calls.push('notifyJoined');
    return bridgeOk(undefined);
  }

  onPcmPort(handler: (port: MessagePort) => void): void {
    this.pcmPortHandler = handler;
  }

  onDeviceState(handler: (state: UsbDeviceState) => void): void {
    this.deviceStateHandler = handler;
  }

  async currentStethoscopeState(): Promise<BridgeResult<UsbDeviceState>> {
    return this.initialStethoscopeState === null
      ? bridgeFail('mock: no initial stethoscope state configured')
      : bridgeOk(this.initialStethoscopeState);
  }

  onStethoscopeError(handler: (message: string) => void): void {
    this.errorHandler = handler;
  }

  onAudioRouteRestored(handler: (message: string) => void): void {
    this.routeRestoredHandler = handler;
  }

  dispose(): void {
    this.stopGenerator();
    this.closeChannel();
    this.pcmPortHandler = null;
    this.deviceStateHandler = null;
    this.errorHandler = null;
    this.routeRestoredHandler = null;
  }

  /** Simulate the host losing and restoring the output route. */
  emitRouteRestored(message = 'output route was lost and has been restored'): void {
    this.routeRestoredHandler?.(message);
  }

  // ---- test and development controls ----

  /** Hand the app a binary PCM port, as a D3-capable host would. */
  openPcmPort(): MessagePort {
    this.channel = new MessageChannel();
    this.pcmPortHandler?.(this.channel.port2);
    return this.channel.port1;
  }

  /**
   * Emit a synthetic tone at the given frequency.
   *
   * 50 Hz by default because that is the band the DSP must preserve — it is the
   * signal test 2 asserts survives the graph, and the one a developer can check
   * by ear without a stethoscope or a patient.
   */
  startToneGenerator(frequencyHz = 50, sampleRate = 48_000): void {
    const port = this.openPcmPort();
    const framesPerBuffer = 4096;
    const intervalMs = (framesPerBuffer / sampleRate) * 1000;
    let phase = 0;

    this.generator = window.setInterval(() => {
      const samples = new Float32Array(framesPerBuffer);
      for (let i = 0; i < framesPerBuffer; i++) {
        samples[i] = Math.sin(phase) * 0.5;
        phase += (2 * Math.PI * frequencyHz) / sampleRate;
        if (phase > 2 * Math.PI) phase -= 2 * Math.PI;
      }
      port.postMessage(encodeFrame(this.sequence++, sampleRate, samples));
    }, intervalMs);
  }

  emitDeviceState(state: UsbDeviceState): void {
    this.deviceStateHandler?.(state);
  }

  emitError(message: string): void {
    this.errorHandler?.(message);
  }

  isSessionActive(): boolean {
    return this.sessionActive;
  }

  isStethoscopeRunning(): boolean {
    return this.stethoRunning;
  }

  private stopGenerator(): void {
    if (this.generator !== null) {
      window.clearInterval(this.generator);
      this.generator = null;
    }
  }

  private closeChannel(): void {
    if (this.channel) {
      this.channel.port1.close();
      this.channel.port2.close();
      this.channel = null;
    }
  }
}
